import React, { createContext, useContext, useState, useEffect } from 'react';
import authServiceWrapper from '../services/AuthServiceWrapper';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user and profile on mount
  useEffect(() => {
    console.log('🔐 AuthContext: useEffect triggered');
    
    // Get initial state immediately
    const currentUser = authServiceWrapper.currentUser;
    const isLoading = authServiceWrapper.isLoading;
    
    console.log('🔐 AuthContext: Initial state - user:', currentUser?.email || 'null', 'loading:', isLoading);
    setUser(currentUser);
    setUserProfile(currentUser);
    setLoading(isLoading);
    
    // Listen for auth changes using the existing auth service
    const listener = (user, isLoading) => {
      console.log('🔐 AuthContext: Listener called with user:', user?.email || 'null', 'loading:', isLoading);
      
      setUser(user);
      setUserProfile(user);
      setLoading(isLoading);
      console.log('🔐 AuthContext: State updated - user:', user?.email || 'null', 'loading:', isLoading);
    };

    // Add listener to the auth service wrapper
    authServiceWrapper.addListener(listener);

    return () => {
      authServiceWrapper.removeListener(listener);
    };
  }, []);

  const signUp = async (email, password, userData) => {
    try {
      console.log('AuthContext signUp called with:', { email, userData });
      
      const result = await authServiceWrapper.register({
        email,
        password,
        username: userData.username,
        user_type: userData.user_type,
        city: userData.city,
        state: userData.state
      });
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result;
    } catch (error) {
      console.error('AuthContext signUp error:', error);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      console.log('AuthContext signIn called with:', { email });
      
      const result = await authServiceWrapper.login(email, password);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result.user;
    } catch (error) {
      console.error('AuthContext signIn error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await authServiceWrapper.logout();
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      const result = await authServiceWrapper.resetPassword(email);
      if (!result.success) {
        throw new Error(result.error);
      }
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const result = await authServiceWrapper.resendConfirmation(email);
      if (!result.success) {
        throw new Error(result.error);
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      const result = await authServiceWrapper.updateProfile(updates);
      if (result.success) {
        // Refresh user data
        const currentUser = authServiceWrapper.currentUser;
        setUser(currentUser);
        setUserProfile(currentUser);
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resendVerificationEmail,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: userProfile?.isAdmin || false,
    canModerate: userProfile?.isAdmin || (userProfile?.scenePoints >= 100) || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 